import { ButtonBase, IconButton, MenuItem, Paper } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

// ─── Trigger ──────────────────────────────────────────────────────────────────

/**
 * Default trigger button. Used when `renderTrigger` is not provided.
 * Intentionally unstyled so it inherits whatever wrapper style is needed;
 * callers are expected to style via `className` or compose inside e.g.
 * `MenuButton`.
 */
export const TriggerButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'fullWidth',
})<{ isActive?: boolean; fullWidth?: boolean }>(({ theme, isActive, fullWidth }) => ({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 8,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  justifyContent: 'flex-start',
  width: fullWidth ? '100%' : undefined,

  '&:hover': {
    boxShadow: `0 0 0 1px ${theme.palette.divider}`,
  },
  ...(isActive && {
    boxShadow: `0 0 0 1px ${theme.palette.divider}`,
  }),
}));

// ─── Popover shell ────────────────────────────────────────────────────────────

export const PopoverContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh',
  overflow: 'hidden',
});

// ─── Header row ("Templates for requests" / title) ───────────────────────────

export const SectionTitle = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  // The header used to take its height from `TitleIconButton` (24px + 2px padding, so
  // ~36px with the 4px block padding). A title with NO icon collapsed to ~22px — the
  // text sitting tight against the border, visibly shallower than the rows beneath it.
  // 32px is the floor for the bare case; the icon case already clears it and is unchanged.
  minHeight: 32,
  boxSizing: 'border-box',
  padding: '4px 8px 4px 16px',
  color: alpha(theme.palette.grey[0], 0.7),
  fontSize: 12,
  fontWeight: 700,
  userSelect: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const GroupTitle = styled('div')(({ theme }) => ({
  padding: 8,
  color: alpha(theme.palette.grey[0], 0.7),
  fontSize: 12,
  fontWeight: 700,
  userSelect: 'none',
}));

export const TitleIconButton = styled(IconButton)(({ theme }) => ({
  padding: 2,
  color: theme.palette.grey[50],
  minWidth: 24,
  minHeight: 24,
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.primary, 0.06),
  },
}));

// ─── Item list ────────────────────────────────────────────────────────────────

export const ListWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: 8,
  overflowY: 'auto',
});

export const EmptyState = styled('div')(({ theme }) => ({
  padding: '12px 8px',
  fontSize: 12,
  fontWeight: 600,
  color: alpha(theme.palette.text.primary, 0.6),
  textAlign: 'center',
}));

/**
 * A single row inside the list.
 * The `.item-action-button` class is used to show the submenu trigger on
 * hover – keeping the same convention as `RequestsMenu`. Keyboard focus reveals it
 * too: Tab lands on those buttons, and an invisible focused control is a dead end.
 */
export const ListRow = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'clickable' && prop !== 'isDanger' && prop !== 'isActive',
})<{ clickable?: boolean; isDanger?: boolean; isActive?: boolean }>(
  ({ theme, clickable, isDanger, isActive }) => ({
    // No `gap` here on purpose: the 8px row gap now comes from the global `MuiMenuItem` override
    // (mui.theme.tsx), so this menu and every Select/Menu dropdown space their icons identically.
    cursor: clickable ? 'pointer' : 'default',
    backgroundColor: isActive ? theme.palette.grey[120] : 'transparent',
    // Only the danger colour is set here. The row's normal ink is the theme's
    // `MuiMenuItem` colour — `text.secondary` #414E55, which is what Figma's
    // `modal-btn` uses. This used to force `text.primary` (#2C3A42), one step
    // darker, and ONLY on the parent panel: submenu rows go through
    // `StyledMenuItem`, which never overrode it, so the two panels of one menu
    // rendered their labels in different inks.
    ...(isDanger && { color: theme.palette.error.main }),

    '& .item-action-button': {
      opacity: 0,
      transition: theme.transitions.create('opacity'),
    },
    '&:hover .item-action-button, & .item-action-button:focus-within': {
      opacity: 1,
    },
    ...(isActive && {
      '&:hover': {
        backgroundColor: theme.palette.grey[120],
      },
      '& .item-action-button': {
        opacity: 1,
      },
    }),
  }),
);

/**
 * Hover target for a row that carries a `tooltip`.
 *
 * A disabled MUI row is `pointer-events: none` (ButtonBase), so it never fires the
 * enter/leave a Tooltip listens for — and a Tooltip put on the label INSIDE the row
 * inherits that dead zone as well as anchoring the bubble to the text rather than the
 * row. Wrapping instead: the events pass through the row and land on this span, which
 * spans the full row and gives the bubble the row's own rect to point at.
 */
export const RowTooltipTarget = styled('span')({
  display: 'block',
});

/**
 * The submenu surface.
 *
 * Was MUI `Menu`'s own paper (with an `mt: -1` nudge to cancel the list padding). It is a plain
 * `Paper` now because the submenu is positioned by a `Popper` against the popover PANEL rather than
 * anchored to the ⋮ button — see the positioning note in `index.tsx`. Keep the visual values in
 * sync with `PopoverContainer`'s surface so the two panels read as one object.
 */
export const SubmenuPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  /**
   * Load-bearing, and the triple-& is not cargo cult.
   *
   * `mui.theme.tsx` has a global `MuiPopper` override — `root: { '& .MuiPaper-root': { marginTop:
   * 4 } }` — which gives every Popper-based dropdown a 4px gap below its trigger. Popper cannot see
   * that margin: it positions the WRAPPER correctly, the margin then pushes this panel 4px down
   * inside it, and the submenu's top lands 4px below the row it is supposed to line up with.
   * Measured before this reset: wrapper top 455.13 (right), paper top 459.
   *
   * A plain `margin: 0` loses — the theme's rule is a descendant selector (0,2,0) and this styled
   * class is (0,1,0) — so the reset is scoped up to (0,3,0). The global override stays untouched:
   * every other Popper in the app still wants that 4px.
   */
  '&&&': { marginTop: 0 },
}));

/**
 * The submenu's list inset — deliberately the mirror image of `ListWrapper`, not a
 * `padding` on `SubmenuPaper`.
 *
 * Figma (37867-47295) draws the two panels of one menu at the SAME width with the
 * same 8px inset; the submenu used to be `minWidth: 200` with a 4px pad, so it came
 * out both narrower and more tightly packed than the panel it belonged to. Putting
 * the padding on an inner box exactly as the parent does makes the two papers
 * identical by construction — padding inside the declared width, border outside it —
 * instead of by an arithmetic correction that would drift the moment either padding
 * changed.
 *
 * The panel's width arrives as a `minWidth` (see the call site), so equal-width is a
 * FLOOR, not a cap: a submenu row that needs more than the panel offers — a status
 * name plus its italic "via sign-off" hint inside a 220px chip menu — widens this
 * paper instead of painting outside it. Rows still carry `ItemText`'s ellipsis, so a
 * caller that DOES cap the width clips its labels rather than overflowing.
 *
 * The 200 is the floor for a caller whose panel width is `'auto'` and therefore has
 * no number to mirror.
 */
export const SubmenuList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: 8,
  minWidth: 200,
  boxSizing: 'border-box',
});

export const StyledDivider = styled('div')(({ theme }) => ({
  height: 1,
  margin: '4px 0',
  backgroundColor: theme.palette.divider,
}));

export const ItemText = styled('span')({
  fontWeight: 600,
  flexGrow: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

// ─── Hidden submenu trigger ───────────────────────────────────────────────────

export const ActionIconWrapper = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  minHeight: 20,
  borderRadius: 4,
  flexShrink: 0,
  padding: 0,
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

/**
 * The `staticSubmenuIcon` variant of the above: a bare glyph slot for rows whose
 * submenu opens on a row click, so the chevron needs no hit target of its own and
 * must not fade out at rest the way `.item-action-button` does.
 */
export const StaticSubmenuIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: theme.palette.grey[50],
}));

// ─── Submenu items ────────────────────────────────────────────────────────────

export const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isDanger',
})<{ isDanger?: boolean }>(({ theme, isDanger }) => ({
  ...(isDanger && {
    color: theme.palette.error.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.error.main,
    },
    '& .MuiListItemText-primary': {
      color: theme.palette.error.main,
    },
  }),
}));
